import React, { useEffect, useState } from 'react';
import { Button, message, Input, Select, Modal } from 'antd';
import DatePickerComponent from '../DatePicker';
import { postRequest } from '../../utils/APICall';
import { useSelector } from 'react-redux';
import { SelectDepart } from '../SelectInput';
import moment from 'moment';

const LeaveForm = ({ editingData, onSuccess, resetEditing }) => {
  const [department, setDepartment] = useState({ id: '', title: '' });
  const [leaveType, setLeaveType] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [leaveCount, setLeaveCount] = useState('');
  const { allDept } = useSelector((state) => state.employees);
  const { users } = useSelector((state) => state.user || {});

  useEffect(() => {
    if (editingData) {
      setDepartment({
        id: editingData?.FKDEPARTMENTID,
        title: editingData?.TITLE
      });
      setLeaveType(editingData.FKLEAVETYPES === 1 ? 'Annualleave' : 'sickleave');
      setFromDate(moment(editingData.FROMDATE).format("DD-MMMM-YYYY"));
      setToDate(moment(editingData.TODATE).format("DD-MMMM-YYYY"));
      setLeaveCount(editingData.LEAVES);
    }
  }, [editingData]);

  const resetForm = () => {
    setDepartment({ id: "", title: "" });
    setLeaveType(null);
    setFromDate('');
    setToDate('');
    setLeaveCount('');
    resetEditing?.();
  };

  const isFormValid = () => {
    return (
      department &&
      leaveType &&
      leaveCount &&
      fromDate &&
      toDate
    );
  };

  const uniqueDepartments = allDept && [...new Map(allDept.map(item => [item.DEPT.trim() + item.CODE, { DEPT: item.DEPT.trim(), CODE: item.CODE }])).values()];

  const handleSubmit = async () => {
    if (!department || !leaveType || !leaveCount || !fromDate || !toDate) {
      message.error("Please fill all required fields before submitting.");
      return;
    }
    const payload = {
      vtodate: toDate,
      vfromdate: fromDate,
      vleaves: Number(leaveCount),
      vdptid: typeof department === 'object' && department !== null && 'id' in department
        ? department.id
        : department,
      vLeavetype: leaveType === 'Annualleave' ? 1 : 2,
      vCreatedby: `${users?.FIRSTNAME} ${users?.LASTNAME} | ${moment(Date.now()).format("DD-MM-YYYY HH:mm")}`,
      veditid: editingData?.ID || 0,
    };

    const res = await postRequest('addLeaveCount', payload);
    if (res.success) {
      message.success(editingData ? "Leave updated successfully!" : "Leave submitted successfully!");
      onSuccess?.();
      resetForm();
    } else {
      message.error("Something went wrong. Please try again.");
    }
  };

  const showConfirm = () => {
    Modal.confirm({
      title: editingData ? 'Confirm Update' : 'Confirm Submission',
      content: editingData ? 'Are you sure you want to update Financial year leave?' : 'Are you sure you want to add Financial year leave?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: handleSubmit,
    });
  };

  return (
    <div className='w-full bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-md dark:shadow-gray-800 mb-6 '>
      <div className='flex w-full flex-wrap gap-4'>

        {/* Department */}
        <div className="w-full sm:w-[200px]">
          <label className='block font-semibold mb-1 text-gray-700 dark:text-gray-300'>
            Department Title <span className="text-red-500">*</span>
          </label>
          <SelectDepart
            allLabel={{}}
            departList={uniqueDepartments}
            onChange={setDepartment}
            selectedDepartment={department.title || ""}
          />
        </div>

        {/* Leave Type */}
        <span className="w-full sm:w-fit">
          <label className='block font-semibold mb-1 text-gray-700 dark:text-gray-300'>
            Leave Type <span className="text-red-500">*</span>
          </label>
          <Select
            showSearch
            placeholder="--Select Leave Type--"
            onChange={setLeaveType}
            value={leaveType}
            size='large'
            className='sm:w-[250px] w-full '
            options={[
              { value: 'Annualleave', label: 'Annual Leave' },
              { value: 'sickleave', label: 'Sick Leave' },
            ]}
          />
        </span>

        {/* Leaves */}
        <span className="w-full sm:w-fit">
          <label className='block font-semibold mb-1 text-gray-700 dark:text-gray-300'>
            Leaves <span className="text-red-500">*</span>
          </label>
          <Input
            type='number'
            value={leaveCount}
            onChange={(e) => setLeaveCount(e.target.value)}
            placeholder="Enter number of leaves"
            size='large'
            className='sm:w-[250px] w-full '
          />
        </span>

        {/* From Date */}
        <span className="w-full sm:w-fit">
          <label className='block font-semibold mb-1 text-gray-700 dark:text-gray-300'>
            From Date <span className="text-red-500">*</span>
          </label>
          <DatePickerComponent
            onChange={(date, dateString) => setFromDate(dateString)}
            date={fromDate}
            customStyle="h-10 w-full sm:w-[250px] "
          />
        </span>

        {/* To Date */}
        <span className="w-full sm:w-fit">
          <label className='block font-semibold mb-1 text-gray-700 dark:text-gray-300'>
            To Date <span className="text-red-500">*</span>
          </label>
          <DatePickerComponent
            onChange={(date, dateString) => setToDate(dateString)}
            date={toDate}
            customStyle="h-10 w-full sm:w-[250px] "
          />
        </span>

      </div>

      {/* Buttons */}
      <div className='flex justify-end gap-4 mt-6'>
        <span className="w-full sm:w-fit">
          <Button
            className='bg-green-500 hover:bg-green-600 text-white font-semibold rounded-md p-4 px-12'
            size="large"
            onClick={resetForm}
          >
            Clear
          </Button>
        </span>

        <Button
          color="primary"
          size="large"
          className='text-lg font-bold text-gray-100 disabled:text-white disabled:bg-gray-500 p-4 px-12 dark:bg-blue-500 dark:hover:bg-blue-600'
          variant="solid"
          onClick={showConfirm}
          disabled={!isFormValid()}
        >
          {editingData ? "Update" : "Submit"}
        </Button>
      </div>
    </div>

  );
};

export default LeaveForm;